import { useState, type FormEvent, type ReactElement } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from './AuthProvider';
import { ApiError } from '../../lib/api';
import { AuthLayout, AuthField, AuthSubmit, AuthError } from './AuthLayout';

export default function Register(): ReactElement {
  const { register } = useAuth();
  // Werbecode aus dem Link (/register?ref=ABC). Das Backend liest
  // zusaetzlich ein Cookie, falls zwischen Klick und Registrierung Tage
  // liegen und der Parameter verloren ging.
  const [params] = useSearchParams();
  const ref = params.get('ref') ?? undefined;
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError('Das Passwort muss mindestens 8 Zeichen lang sein.');
      return;
    }

    if (!acceptedTerms) {
      setError('Bitte stimme den AGB und der Datenschutzerklärung zu.');
      return;
    }

    setBusy(true);
    try {
      await register(email, password, name.trim() || undefined, ref);
      navigate('/app', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Registrierung fehlgeschlagen. Bitte erneut versuchen.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout title="Konto erstellen" subtitle="In wenigen Sekunden startklar mit Saldify.">
      <form onSubmit={handleSubmit} className="space-y-5" noValidate>
        {error && <AuthError message={error} />}
        <AuthField
          label="Name (optional)"
          type="text"
          autoComplete="name"
          value={name}
          onChange={setName}
          placeholder="Max Mustermann"
        />
        <AuthField
          label="E-Mail-Adresse"
          type="email"
          autoComplete="email"
          value={email}
          onChange={setEmail}
          placeholder="name@firma.de"
          required
        />
        <AuthField
          label="Passwort"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={setPassword}
          placeholder="Mindestens 8 Zeichen"
          hint="Wähle ein Passwort mit mindestens 8 Zeichen."
          required
        />
        <label className="flex items-start gap-3 text-sm text-gray-600">
          <input
            type="checkbox"
            checked={acceptedTerms}
            onChange={(e) => setAcceptedTerms(e.target.checked)}
            className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            required
          />
          <span>
            Ich akzeptiere die{' '}
            <a
              href="/agb"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-blue-600 hover:text-blue-700"
            >
              AGB
            </a>{' '}
            und die{' '}
            <a
              href="/datenschutz"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-blue-600 hover:text-blue-700"
            >
              Datenschutzerklärung
            </a>
            .
          </span>
        </label>
        <AuthSubmit busy={busy} disabled={!acceptedTerms}>
          Konto erstellen
        </AuthSubmit>
      </form>
      <p className="mt-6 text-center text-sm text-gray-600">
        Bereits registriert?{' '}
        <Link to="/login" className="font-semibold text-blue-600 hover:text-blue-700">
          Anmelden
        </Link>
      </p>
    </AuthLayout>
  );
}
